import api, { fetchCloudAuthenticated } from './api';
import {
  probeLocalLibraryHost,
  probeLocalLibraryIpc,
  routeLocalLibrary,
  shouldUseLocalLibraryIpc,
  isHomeLibraryHost,
  ensureLibraryRoutingReady,
  isRemoteLibraryViewer,
  isRemoteConnectorShell,
  isLibraryHostProbeComplete,
  setRemoteHomeLibraryAvailable,
  markRemoteHomeLibraryFromCatalog,
  markCachedRemoteLibraryHint,
  isTauriRuntimeAvailable,
} from './localLibraryIpcGate';
import {
  loadLocalAlbumMetadata,
  patchLocalAlbumMetadata,
  patchLocalArtistMetadata,
  isLocalLibraryAlbumKey,
  normalizeLocalAlbumId,
  normalizeLocalArtistId,
  isLocalEntityHash,
  isLocalLibraryArtistKey,
} from './localLibraryMetadata';
import { useLibraryCacheStore } from '../store/libraryCacheStore';
import { useMaestroStore } from '../store/maestroStore';
import { TauriLocalLibraryAdapter } from './localLibrary/TauriLocalLibraryAdapter';
import { withLocalLibraryAlbumsIpcPending } from './localLibraryAlbumsIpcPending';
import {
  getCachedLocalAlbumDetail,
  setCachedLocalAlbumDetail,
} from './albumDetailCache';
import { purgeStaleLocalAlbumRef } from './staleLocalAlbumPurge';
import {
  connectorSupportsLocalRemoveAll,
  connectorSupportsLocalSourceSummary,
  connectorSupportsResetCatalog,
  removeAllRequiresConnectorMessage,
} from '../utils/connectorVersion';
import { connectorInvoke } from '../lib/connector';

export {
  probeLocalLibraryIpc,
  probeLocalLibraryHost,
  isHomeLibraryHost,
  isRemoteLibraryViewer,
  isRemoteConnectorShell,
  isLibraryHostProbeComplete,
  setRemoteHomeLibraryAvailable,
  hasRemoteHomeLibraryAvailable,
  markRemoteHomeLibraryFromCatalog,
  markCachedRemoteLibraryHint,
  ensureLibraryRoutingReady,
} from './localLibraryIpcGate';
import type {
  LocalLibraryAdapter,
  LocalLibraryCapabilities,
} from './localLibrary/LocalLibraryAdapter';

let _tauriDetected: boolean | null = null;
let _localAdapter: LocalLibraryAdapter | null = null;
let _localCapabilitiesPromise: Promise<LocalLibraryCapabilities> | null = null;
let _localHealthStatusPromise: Promise<any> | null = null;

function isTauriRuntimeAvailable(): boolean {
  if (_tauriDetected !== null) return _tauriDetected;
  const w = window as any;
  _tauriDetected = typeof window !== 'undefined' && !!(w?.__TAURI_INTERNALS__ || w?.__TAURI__);
  return _tauriDetected;
}

function getLocalAdapter(): LocalLibraryAdapter {
  if (_localAdapter) return _localAdapter;
  _localAdapter = new TauriLocalLibraryAdapter();
  return _localAdapter;
}

type LocalAlbumDetailResponse = {
  success: boolean;
  album?: Record<string, unknown>;
  tracks?: Record<string, unknown>[];
  version?: number;
  error?: string;
};

const localGetAlbumInflight = new Map<string, Promise<LocalAlbumDetailResponse>>();

/** True while a local-library getAlbum IPC is in flight for this id (album-open single-flight). */
export function isLocalAlbumGetInFlight(albumKey: string): boolean {
  const normalized = normalizeLocalAlbumId(String(albumKey || '').trim());
  return normalized ? localGetAlbumInflight.has(normalized) : false;
}

function localAlbumDetailHasBody(detail: { album?: { album_key?: string; id?: string; title?: string } }): boolean {
  const album = detail.album;
  if (!album) return false;
  const key = String(album.album_key ?? album.id ?? '').trim();
  const title = String(album.title ?? '').trim();
  return !!(key || title);
}

async function fetchLocalAlbumDetailOnce(
  albumKey: string,
): Promise<LocalAlbumDetailResponse> {
  const normalized = normalizeLocalAlbumId(albumKey);
  const cached = getCachedLocalAlbumDetail(normalized);
  if (cached) return cached;

  const existing = localGetAlbumInflight.get(normalized);
  if (existing) return existing;

  const promise = (async (): Promise<LocalAlbumDetailResponse> => {
    const detail = await getLocalAdapter().getAlbum(normalized);
    const hasBody = localAlbumDetailHasBody(detail);
    if (!detail.success && !hasBody) {
      purgeStaleLocalAlbumRef(normalized);
      return {
        success: false,
        error: 'not_found',
        album: undefined,
        tracks: [],
        version: detail.version,
      };
    }
    const resp: LocalAlbumDetailResponse = {
      success: true,
      album: detail.album,
      tracks: detail.tracks ?? [],
      version: detail.version,
    };
    setCachedLocalAlbumDetail(normalized, resp);
    return resp;
  })();

  localGetAlbumInflight.set(normalized, promise);
  try {
    return await promise;
  } finally {
    if (localGetAlbumInflight.get(normalized) === promise) {
      localGetAlbumInflight.delete(normalized);
    }
  }
}

async function getLocalCapabilities(): Promise<LocalLibraryCapabilities> {
  if (!_localCapabilitiesPromise) {
    _localCapabilitiesPromise = getLocalAdapter().getCapabilities().catch(() => ({
      enrichment: false,
      bulkRepair: false,
      duplicateManagement: false,
      folderManagement: false,
      scanStatus: false,
      artworkManagement: false,
    }));
  }
  return _localCapabilitiesPromise;
}

async function getLocalHealthStatusCached() {
  if (!_localHealthStatusPromise) {
    _localHealthStatusPromise = getLocalAdapter().getHealthStatus().catch(() => ({
      status: 'unknown',
      message: null,
      lastRunAt: null,
      mappedLegacyCount: null,
      unresolvedLegacyCount: null,
    }));
  }
  return _localHealthStatusPromise;
}

function isLocalOnlySourceId(sourceId?: string): boolean {
  if (!sourceId) return false;
  const s = String(sourceId).toLowerCase();
  return s.startsWith('local:') || s.startsWith('src-') || s.includes('local');
}

function isLocalEntityId(id?: string): boolean {
  if (!id) return false;
  const s = String(id);
  if (isLocalEntityHash(s)) return true;
  return /^local:(album|artist|track):[0-9a-f]{16}$/i.test(s);
}

function shouldRouteLocalByParams(params?: {
  source_id?: string;
  sourceId?: string;
  album?: string;
  search?: string;
}): boolean {
  if (!shouldUseLocalLibraryIpc()) return false;
  if (!params) return false;
  const sourceId = (params as any).source_id ?? (params as any).sourceId;
  if (isLocalOnlySourceId(sourceId)) return true;
  if (isLocalEntityId((params as any).album)) return true;
  return false;
}

function shouldRouteLocalById(id?: string): boolean {
  if (!shouldUseLocalLibraryIpc()) return false;
  if (!id) return false;
  return isLocalEntityId(id) || String(id).startsWith('local:');
}


/** Fetch album detail from home library via Mycelium tunnel proxy. */
async function fetchRemoteAlbumDetail(
  albumKey: string,
  options?: { machineId?: string | null; title?: string; artist?: string },
): Promise<any | null> {
  const machineId = options?.machineId || null;
  const queryFirst = [
    ...(machineId
      ? [
          `/api/library/remote/album?album_id=${encodeURIComponent(albumKey)}&machine_id=${encodeURIComponent(machineId)}`,
          `/api/library/remote/album?album_id=${encodeURIComponent(albumKey)}`,
        ]
      : []),
    `/api/library/remote/album?album_id=${encodeURIComponent(albumKey)}`,
    `/api/library/remote/album/${encodeURIComponent(albumKey)}${machineId ? `?machine_id=${encodeURIComponent(machineId)}` : ''}`,
    `/api/library/remote/album/${encodeURIComponent(albumKey)}`,
  ];
  const seen = new Set<string>();
  const paths = queryFirst.filter((path) => {
    if (seen.has(path)) return false;
    seen.add(path);
    return true;
  });

  for (const path of paths) {
    try {
      const remote = await api.get(path);
      if (remote?.success && Array.isArray(remote?.tracks)) {
        markRemoteHomeLibraryFromCatalog(1);
        markCachedRemoteLibraryHint(true);
        try {
          useMaestroStore.getState().setTunnelConnected(true);
        } catch { /* optional */ }
        return remote;
      }
      if (remote?.success && remote?.album) {
        // Album metadata without tracks — keep trying other paths.
        console.warn('[libraryService] remote album missing tracks, retrying:', path);
      }
    } catch (e) {
      console.warn('[libraryService] remote album fetch failed:', path, e);
    }
  }

  // Title/artist fallback — remote catalog search param is not wired; scan one page.
  const title = (options?.title || '').trim();
  const artist = (options?.artist || '').trim();
  if (title) {
    try {
      const qs = new URLSearchParams({ limit: '500', offset: '0' });
      if (machineId) qs.set('machine_id', machineId);
      const catalog = await api.get(`/api/library/remote/catalog?${qs.toString()}`);
      const candidates = catalog?.albums || catalog?.items || [];
      const match = candidates.find((a: any) => {
        const t = (a?.title || '').trim().toLowerCase();
        const ar = (a?.artist_name || a?.album_artist || '').trim().toLowerCase();
        return t === title.toLowerCase() && (!artist || ar === artist.toLowerCase());
      });
      if (match) {
        const retryKey = match.album_key || match.id;
        if (retryKey && retryKey !== albumKey) {
          return fetchRemoteAlbumDetail(retryKey, {
            machineId: match.machine_id || machineId,
            title,
            artist,
          });
        }
      }
    } catch (e) {
      console.warn('[libraryService] remote album title fallback failed:', e);
    }
  }

  try {
    useMaestroStore.getState().setTunnelConnected(false);
  } catch { /* optional */ }
  return null;
}

/** When cloud returns no albums but we have a persisted cache, keep showing it. */
function albumsResponseWithCacheFallback<T extends { albums?: unknown[]; total?: number; version?: string | null }>(
  resp: T,
): T {
  const albums = Array.isArray(resp?.albums) ? resp.albums : [];
  if (albums.length > 0) return resp;

  const cached = useLibraryCacheStore.getState();
  // After wipe the cache is cleared — never resurrect stale albums from localStorage.
  if (cached.albums.length === 0) return resp;
  // Home Connector: cached cloud albums are not the local catalog — show empty state instead.
  if (isTauriRuntimeAvailable()) return resp;
  const cachedAlbums = cached.albums || [];
  if (cachedAlbums.length === 0) return resp;

  // Remote viewer with a dead tunnel — mark offline so UI can warn; cache is display-only.
  const remoteShell = isRemoteConnectorShell()
    || (isLibraryHostProbeComplete() && isRemoteLibraryViewer());
  if (remoteShell) {
    markCachedRemoteLibraryHint(true);
    setRemoteHomeLibraryAvailable(false);
    try {
      useMaestroStore.getState().setTunnelConnected(false);
    } catch { /* optional */ }
    console.warn(
      '[libraryService] Home library unreachable — showing',
      cachedAlbums.length,
      'cached albums (open the album on your home Mac to refresh the tunnel)',
    );
    return {
      ...resp,
      albums: cachedAlbums,
      total: cachedAlbums.length,
      version: cached.version ?? resp?.version ?? null,
      __fromCache: true,
      __tunnelOffline: true,
    } as T;
  }

  console.warn(
    '[libraryService] Server returned 0 albums — using',
    cachedAlbums.length,
    'cached entries (remote viewer cache)',
  );
  markCachedRemoteLibraryHint(true);
  return {
    ...resp,
    albums: cachedAlbums,
    total: cachedAlbums.length,
    version: cached.version ?? resp?.version ?? null,
    __fromCache: true,
  } as T;
}

export const libraryService = {
  // Sources
  getSources: () =>
    routeLocalLibrary(
      () => getLocalAdapter().listSources().then((sources) => ({ sources })),
      () => api.get('/api/library/sources'),
    ),
  createSource: (data: { path: string; label?: string; is_temporary?: boolean; machine_id?: string }) =>
    routeLocalLibrary(
      () => getLocalAdapter().addSource({
        path: data.path,
        label: data.label,
        machine_id: data.machine_id,
      }),
      () => api.post('/api/library/sources', data),
    ),
  createSources: (items: Array<{ path: string; label?: string; is_temporary?: boolean; machine_id?: string }>) =>
    routeLocalLibrary(
      () => getLocalAdapter().addSources(items.map((item) => ({
        path: item.path,
        label: item.label,
        machine_id: item.machine_id,
      }))),
      async () => {
        const results = [];
        for (const item of items) {
          const resp = await api.post('/api/library/sources', item);
          results.push({ success: true, source: resp?.data?.source || resp?.source });
        }
        return { success: true, results, count: results.length };
      },
    ),
  updateSource: (id: string, data: Record<string, any>) =>
    api.patch(`/api/library/sources/${encodeURIComponent(id)}`, data),
  deleteSource: (id: string, deleteTracks = true) =>
    routeLocalLibrary(
      () => getLocalAdapter().removeSource(id, deleteTracks),
      () => api.delete(`/api/library/sources/${encodeURIComponent(id)}?delete_tracks=${deleteTracks}`),
    ),
  /** @deprecated — use removeAllChunked() instead */
  removeAllLegacy: () => api.delete('/api/library/all'),

  deleteSourceChunk: (sourceId: string, limit = 500) =>
    api.post(`/api/library/sources/${encodeURIComponent(sourceId)}/delete-chunk?limit=${limit}`),

  removeAllChunked: async (onProgress?: (p: {
    currentSource: number;
    totalSources: number;
    sourceName: string;
    tracksRemoved: number;
    sourceComplete: boolean;
  }) => void) => {
    if (shouldUseLocalLibraryIpc()) {
      if (!connectorSupportsLocalRemoveAll()) {
        throw new Error(removeAllRequiresConnectorMessage());
      }
      const sourcesResp = await libraryService.getSources();
      const sources: { id: string; path?: string; label?: string }[] = sourcesResp?.sources || [];
      onProgress?.({
        currentSource: 0,
        totalSources: Math.max(sources.length, 1),
        sourceName: 'Local library',
        tracksRemoved: 0,
        sourceComplete: false,
      });
      const result = await getLocalAdapter().removeAll();
      onProgress?.({
        currentSource: Math.max(sources.length, 1),
        totalSources: Math.max(sources.length, 1),
        sourceName: 'Complete',
        tracksRemoved: result.tracksDeleted,
        sourceComplete: true,
      });
      return {
        sourcesRemoved: result.sourcesRemoved,
        totalTracksRemoved: result.tracksDeleted,
        errors: [] as string[],
      };
    }

    const sourcesResp = await api.get('/api/library/sources');
    const sources: { id: string; path?: string; label?: string }[] = sourcesResp?.sources || [];
    if (sources.length === 0) return { sourcesRemoved: 0, totalTracksRemoved: 0, errors: [] as string[] };

    let totalTracksRemoved = 0;
    const errors: string[] = [];

    for (let i = 0; i < sources.length; i++) {
      const src = sources[i];
      const name = src.label || src.path?.split('/').pop() || 'Source';
      try {
        let chunkResult: any;
        do {
          chunkResult = await api.post(
            `/api/library/sources/${encodeURIComponent(src.id)}/delete-chunk?limit=500`,
          );
          totalTracksRemoved += chunkResult?.tracks_deleted || 0;
          onProgress?.({
            currentSource: i + 1,
            totalSources: sources.length,
            sourceName: name,
            tracksRemoved: totalTracksRemoved,
            sourceComplete: !!chunkResult?.source_deleted,
          });
        } while (!chunkResult?.source_deleted);
      } catch (e: any) {
        errors.push(`${name}: ${e?.message || 'unknown error'}`);
      }
    }
    return { sourcesRemoved: sources.length - errors.length, totalTracksRemoved, errors };
  },
  rescanSource: (id: string) =>
    routeLocalLibrary(
      () => getLocalAdapter().rescanSource(id),
      () => api.post(`/api/library/sources/${encodeURIComponent(id)}/rescan`),
    ),

  // Ingest (bulk track upload from scanner)
  ingestTracks: (tracks: any[], sourceId?: string, machineId?: string, skipRebuild = false, scanHistoryId?: string) =>
    api.post('/api/library/ingest', {
      tracks,
      source_id: sourceId,
      machine_id: machineId,
      skip_rebuild: skipRebuild,
      ...(scanHistoryId ? { scan_history_id: scanHistoryId } : {}),
    }),

  // Album materialization
  rebuildAlbums: (scanHistoryId?: string) =>
    api.post('/api/library/albums/rebuild', scanHistoryId ? { scan_history_id: scanHistoryId } : {}),

  // Albums (grouped from tracks)
  getAlbums: (params: {
    status?: string;
    sort?: string;
    search?: string;
    format?: string;
    limit?: number;
    offset?: number;
    source_id?: string;
  }) => ensureLibraryRoutingReady().then(() => {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.sort) qs.set('sort', params.sort);
    if (params.search) qs.set('search', params.search);
    if (params.format) qs.set('format', params.format);
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.offset) qs.set('offset', String(params.offset));
    const cloudFallback = async () => {
      // Home Connector never displays cloud/tunnel catalog as the local library grid.
      if (isTauriRuntimeAvailable()) {
        return { albums: [], total: 0, version: null };
      }
      try {
        const remote = await api.get(`/api/library/remote/catalog?${qs.toString()}`);
        const pageAlbums = remote?.albums || remote?.items || [];
        const count = pageAlbums.length || Number(remote?.total) || 0;
        const liveRemote = count > 0
          && !remote?.__fromCache
          && !remote?.__tunnelOffline
          && pageAlbums.some((a: any) => a?.__source === 'remote_tunnel' || a?.isLocalLibrary);
        if (liveRemote) {
          markRemoteHomeLibraryFromCatalog(count);
          try {
            useMaestroStore.getState().setTunnelConnected(true);
          } catch { /* optional */ }
          return remote;
        }
        if (count > 0 && (remote?.__fromCache || remote?.__tunnelOffline)) {
          markCachedRemoteLibraryHint(true);
          try {
            useMaestroStore.getState().setTunnelConnected(false);
          } catch { /* optional */ }
          return remote;
        }
      } catch (e) {
        console.warn('[libraryService] remote tunnel catalog unavailable:', e);
      }
      try {
        useMaestroStore.getState().setTunnelConnected(false);
      } catch { /* optional */ }
      return api.get(`/api/library/albums?${qs.toString()}`).then(albumsResponseWithCacheFallback);
    };

    return routeLocalLibrary(
      () => withLocalLibraryAlbumsIpcPending(() => getLocalAdapter().getAlbums({
        offset: params.offset ?? 0,
        limit: params.limit ?? 200,
        sort: params.sort ?? 'artist',
        filter: params.search || params.status || params.format || '',
        sourceId: params.source_id || undefined,
      }).then((local) => ({
        albums: local.items.map((album) => ({
          ...album,
          isLocalLibrary: true,
          __source: 'local_library',
        })),
        total: local.total,
        total_releases: local.totalReleases,
        version: local.version,
        provisional: local.provisional === true,
        updating: local.updating === true || local.provisional === true,
        catalog_live: local.catalog_live === true,
        snapshot_stale: local.snapshot_stale === true,
        snapshot_captured_at: local.snapshot_captured_at ?? null,
        snapshot_album_count: local.snapshot_album_count ?? null,
      }))),
      cloudFallback,
    );
  }),
  getAlbumsBySource: (sourceId: string, limit = 50) =>
    api.get(`/api/library/albums?source_id=${encodeURIComponent(sourceId)}&limit=${limit}`),
  getAlbumsByPath: (pathPrefix: string, limit = 10) =>
    api.get(`/api/library/albums?path_prefix=${encodeURIComponent(pathPrefix)}&limit=${limit}`),
  getSourceSummary: (sourceId: string) =>
    ensureLibraryRoutingReady().then(async () => {
      if (shouldUseLocalLibraryIpc() && connectorSupportsLocalSourceSummary()) {
        const summary = await getLocalAdapter().getSourceSummary(sourceId);
        return {
          success: true,
          albumCount: summary.albumCount,
          trackCount: summary.trackCount,
          coverArtUrl: summary.coverArtUrl,
        };
      }
      return api.get(`/api/library/source-summary/${encodeURIComponent(sourceId)}`);
    }),
  getAlbum: (albumKey: string, options?: { machineId?: string; title?: string; artist?: string }) =>
    ensureLibraryRoutingReady().then(async () => {
      if (shouldUseLocalLibraryIpc()) {
        return fetchLocalAlbumDetailOnce(albumKey);
      }
      const remote = await fetchRemoteAlbumDetail(albumKey, options);
      if (remote?.success) return remote;

      const isLocalKey = isLocalEntityId(albumKey) || String(albumKey).startsWith('local:');
      if (isLocalKey) {
        return { success: false, error: 'remote_unavailable' };
      }
      try {
        return await api.get(`/api/library/albums/${encodeURIComponent(albumKey)}`);
      } catch {
        return { success: false, error: 'not_found' };
      }
    }),
  getArtists: (params?: { limit?: number; offset?: number }) =>
    routeLocalLibrary(
      () => getLocalAdapter().getArtists({
        limit: params?.limit ?? 500,
        offset: params?.offset ?? 0,
      }).then((r) => ({ artists: r.items, items: r.items, total: r.total, version: r.version })),
      () => api.get('/api/library/artists'),
    ),

  getArtist: (artistId: string) =>
    routeLocalLibrary(
      () => getLocalAdapter().getArtist(normalizeLocalArtistId(artistId)).then((r) => ({
        success: r.success,
        artist: r.artist,
        version: r.version,
      })),
      () => api.get(`/api/library/artists/${encodeURIComponent(artistId)}`),
    ),

  getComposers: (params?: { limit?: number; offset?: number }) =>
    routeLocalLibrary(
      async () => {
        if (!isTauriRuntimeAvailable()) return { composers: [], items: [], total: 0 };
        const payload = await connectorInvoke('local_library_get_composers', {
          params: { limit: params?.limit ?? 500, offset: params?.offset ?? 0 },
        });
        const composers = payload?.composers || payload?.items || [];
        return { composers, items: composers, total: payload?.total ?? composers.length };
      },
      () => api.get('/api/library/remote/catalog?resource=composers'),
      { poisonOnAclFailure: false },
    ),

  getLabels: (params?: { limit?: number; offset?: number }) =>
    routeLocalLibrary(
      async () => {
        if (!isTauriRuntimeAvailable()) return { labels: [], items: [], total: 0 };
        const payload = await connectorInvoke('local_library_get_labels', {
          params: { limit: params?.limit ?? 500, offset: params?.offset ?? 0 },
        });
        const labels = payload?.labels || payload?.items || [];
        return { labels, items: labels, total: payload?.total ?? labels.length };
      },
      () => api.get('/api/library/remote/catalog?resource=labels'),
      { poisonOnAclFailure: false },
    ),
  updateAlbum: (albumKey: string, data: Record<string, any>) =>
    api.patch(`/api/library/albums/${encodeURIComponent(albumKey)}`, data),
  deleteAlbum: (albumKey: string) =>
    routeLocalLibrary(
      () => getLocalAdapter().deleteAlbum(normalizeLocalAlbumId(albumKey)).then((r) => ({
        success: true,
        album_key: r.albumId,
        tracks_removed: r.tracksDeleted,
        version: r.catalogVersion,
      })),
      () => api.delete(`/api/library/albums/${encodeURIComponent(albumKey)}`),
    ),
  rereadAlbum: (albumKey: string) =>
    routeLocalLibrary(
      () => getLocalAdapter().rereadAlbum(normalizeLocalAlbumId(albumKey)),
      () => Promise.reject(new Error('Re-read requires the Maestro desktop app')),
    ),
  removeAlbum: (albumKey: string) =>
    routeLocalLibrary(
      () => getLocalAdapter().removeAlbum(normalizeLocalAlbumId(albumKey)).then((r) => ({
        success: true,
        album_key: r.albumId,
        tracks_removed: r.tracksDeleted,
        files_removed: r.filesDeleted,
        version: r.catalogVersion,
      })),
      () => Promise.reject(new Error('Remove requires the Maestro desktop app')),
    ),
  clearLocalSource: (sourceId: string) => {
    if (!isTauriRuntimeAvailable()) {
      return Promise.reject(new Error('Clear source requires the Maestro desktop app'));
    }
    return getLocalAdapter().clearSource(sourceId);
  },
  wipeLocalLibrary: () => {
    if (!isTauriRuntimeAvailable()) {
      return Promise.reject(new Error('Reset library requires the Maestro desktop app'));
    }
    const adapter = getLocalAdapter();
    if (connectorSupportsResetCatalog()) {
      return adapter.resetCatalog();
    }
    return adapter.wipeAll();
  },
  deleteAlbumsBulk: (albumKeys: string[]) =>
    routeLocalLibrary(
      async () => {
        const adapter = getLocalAdapter();
        let totalDeleted = 0;
        const errors: string[] = [];
        for (const rawKey of albumKeys) {
          try {
            const r = await adapter.deleteAlbum(normalizeLocalAlbumId(rawKey));
            totalDeleted += r.tracksDeleted;
          } catch (e: any) {
            errors.push(`${rawKey}: ${e?.message || String(e)}`);
          }
        }
        return {
          success: true,
          deleted: totalDeleted,
          errors: errors.length ? errors : undefined,
        };
      },
      () => api.post('/api/library/albums/bulk-delete', { album_keys: albumKeys }),
    ),
  repairAlbumFormat: (albumKey: string) =>
    api.post(`/api/library/albums/${encodeURIComponent(albumKey)}/repair-format`),
  formatMismatches: () => api.get('/api/library/format-mismatches'),
  repairAllFormats: () => api.post('/api/library/repair-all-formats'),
  nukeSource: (sourceId: string) =>
    api.post('/api/library/sources/nuke', { source_id: sourceId }),

  // Tracks
  getTrack: (trackKey: string) =>
    routeLocalLibrary(
      () => getLocalAdapter().getTrack(trackKey).then((r) => r.track || r),
      () => api.get(`/api/library/tracks/${encodeURIComponent(trackKey)}`),
    ),
  getTracks: (params?: { search?: string; album?: string; sort?: string; limit?: number; offset?: number }) => {
    const qs = new URLSearchParams();
    if (params?.search) qs.set('q', params.search);
    if (params?.album) qs.set('album', params.album);
    if (params?.sort) qs.set('sort', params.sort);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.offset) qs.set('offset', String(params.offset));
    const query = qs.toString();
    const cloudFallback = () => api.get(`/api/library/tracks${query ? `?${query}` : ''}`);

    if (!params?.album) {
      return routeLocalLibrary(
        () => getLocalAdapter().getTracks({
          sort: params?.sort ?? 'title',
          filter: params?.search || '',
          limit: params?.limit ?? 500,
          offset: params?.offset ?? 0,
        }).then((local) => ({
          tracks: local.items,
          total: local.total,
          version: local.version,
        })),
        cloudFallback,
      );
    }
    return cloudFallback();
  },

  /** Full-text search via local_library_search IPC (Connector home host only). */
  searchLocal: (query: string, limit = 10) => {
    if (!shouldUseLocalLibraryIpc()) {
      return Promise.resolve({ artists: [], albums: [], tracks: [] });
    }
    return routeLocalLibrary(
      () => getLocalAdapter().search({ query, scope: 'all', limit }),
      () => Promise.resolve({ artists: [], albums: [], tracks: [] }),
    ).catch(() => ({ artists: [] as never[], albums: [] as never[], tracks: [] as never[] }));
  },

  // Stats
  getTunnelHealth: (machineId?: string) => {
    const qs = machineId ? `?machine_id=${encodeURIComponent(machineId)}` : '';
    return api.get(`/api/library/tunnel/health${qs}`);
  },

  /** Upsert the home Mac tunnel URL so remote devices can reach the catalog. */
  registerTunnel: (tunnelUrl: string, machineId: string) =>
    api.post('/api/library/tunnel', { tunnel_url: tunnelUrl, machine_id: machineId }),

  getStats: () =>
    routeLocalLibrary(
      () => getLocalAdapter().getStats().then((s) => ({
        success: true,
        albums: s.albums,
        total_albums: s.albums,
        artists: s.artists,
        tracks: s.tracks,
        total_tracks: s.tracks,
        total: s.tracks,
        total_duration_sec: s.totalDurationSec,
        total_size_bytes: s.totalSizeBytes,
        by_format: s.byFormat,
        empty_sources_count: s.emptySourcesCount ?? 0,
        empty_source_labels: s.emptySourceLabels ?? [],
        formats: s.byFormat.reduce((acc, x) => {
          acc[x.format] = x.count;
          return acc;
        }, {} as Record<string, number>),
      })),
      () => api.get('/api/library/stats'),
    ),

  /** Local SQLite stats only — for migration banner vs legacy cloud catalog. */
  getLocalLibraryStats: async () => {
    if (!shouldUseLocalLibraryIpc()) return { tracks: 0, albums: 0 };
    const s = await getLocalAdapter().getStats();
    return { tracks: s.tracks, albums: s.albums };
  },

  // Enrichment
  enrichAlbum: async (albumKey: string, force = false) => {
    if (shouldUseLocalLibraryIpc() && shouldRouteLocalById(albumKey)) {
      return { success: true, skipped: true, reason: 'home_host_local' };
    }
    if (shouldRouteLocalById(albumKey)) {
      const caps = await getLocalCapabilities();
      if (!caps.enrichment) return { success: true, skipped: true, reason: 'capability_disabled' };
    }
    return api.post(`/api/library/enrich/${encodeURIComponent(albumKey)}${force ? '?force=1' : ''}`);
  },
  bulkEnrich: () => api.post('/api/library/bulk-enrich'),
  reEnrich: () => api.post('/api/library/re-enrich'),

  // MusicBrainz search
  searchMusicBrainz: (artist: string, album: string) =>
    api.get(`/api/library/search-mb?artist=${encodeURIComponent(artist)}&album=${encodeURIComponent(album)}`),

  // Full metadata editor
  getAlbumMetadata: (albumKey: string) =>
    isLocalLibraryAlbumKey(albumKey)
      ? loadLocalAlbumMetadata(albumKey)
      : api.get(`/api/library/albums/${encodeURIComponent(albumKey)}/metadata`),
  patchAlbumMetadata: async (albumKey: string, data: Record<string, any>) => {
    if (isLocalLibraryAlbumKey(albumKey)) {
      const album = data.album || data;
      const coverFile = data._coverFile as File | undefined;
      await patchLocalAlbumMetadata(albumKey, {
        title: album.title,
        artist_name: album.artist_name,
        album_artist: album.album_artist,
        year: album.year,
        genre: album.genre,
        cover_art_url: album.cover_art_url,
        disc_count: album.disc_count ?? data.metadata?.album?.disc_count,
      }, { coverFile });
      return { success: true };
    }
    return api.patch(`/api/library/albums/${encodeURIComponent(albumKey)}/metadata`, data);
  },
  enrichArtistImage: (name: string) =>
    api.get(`/api/library/artist-image?name=${encodeURIComponent(name)}`),
  patchArtistMetadata: async (artistKey: string, data: Record<string, any>) => {
    if (isLocalLibraryArtistKey(artistKey)) {
      await patchLocalArtistMetadata(artistKey, data);
      return { success: true };
    }
    return api.patch(`/api/library/artists/${encodeURIComponent(artistKey)}`, data);
  },
  autofillMetadata: (
    albumKey: string,
    context?: {
      title?: string;
      artist_name?: string;
      cover_art_url?: string;
      tracks?: Array<{ id?: string; title?: string; track_num?: number; disc_num?: number; duration?: number }>;
    },
  ) =>
    api.post(`/api/library/albums/${encodeURIComponent(albumKey)}/metadata/autofill`, context ?? {}),

  autofillLyrics: (
    albumKey: string,
    context?: {
      title?: string;
      artist_name?: string;
      tracks?: Array<{ id?: string; title?: string; track_num?: number; disc_num?: number; duration?: number }>;
    },
  ) =>
    api.post(`/api/library/albums/${encodeURIComponent(albumKey)}/metadata/autofill-lyrics`, context ?? {}),

  // Cover art upload (FormData with 'file' field)
  uploadCoverArt: async (albumKey: string, formData: FormData) => {
    const res = await fetchCloudAuthenticated(
      `/api/library/albums/${encodeURIComponent(albumKey)}/cover`,
      { method: 'POST', body: formData },
    );
    if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
    return res.json();
  },

  // ─── Phase 2D: cache, stats, inspector, overrides ──────────────────
  /** Tiny endpoint that returns just `{ version, count, max_updated_at }`. */
  getLibraryVersion: () =>
    routeLocalLibrary(
      () => getLocalAdapter().getLibraryVersion().then((version) => ({ version })),
      () => api.get('/api/library/version'),
    ),
  /** Counts albums by winning cover source. */
  getCoverStats: () =>
    routeLocalLibrary(
      () => getLocalAdapter().getCoverStats().then((s) => ({
        success: true,
        total: s.total,
        withCover: s.withCover,
        withoutCover: s.withoutCover,
        embedded: s.withCover,
        folder: 0,
        online: s.withCover,
        none: s.withoutCover,
        overridden: 0,
      })),
      () => api.get('/api/library/cover-stats'),
    ),
  /**
   * Inspector listing with per-album cover-source visibility.
   * source: 'all' | 'embedded' | 'folder' | 'online' | 'none' | 'overridden' | 'missing' | 'local'
   */
  getInspector: (params?: { source?: string; q?: string; page?: number; pageSize?: number }) => {
    const qs = new URLSearchParams();
    if (params?.source && params.source !== 'all') qs.set('source', params.source);
    if (params?.q) qs.set('q', params.q);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.pageSize) qs.set('page_size', String(params.pageSize));
    const query = qs.toString();
    return api.get(`/api/library/inspector${query ? `?${query}` : ''}`);
  },
  /** Pin (or clear) the cover source for one album. Pass null to clear. */
  setCoverSource: (albumIdOrKey: string | number, source: 'embedded' | 'folder' | 'online' | 'none' | null) =>
    api.post(
      `/api/library/album/${encodeURIComponent(String(albumIdOrKey))}/cover-source`,
      { source },
    ),
  /** Stale-while-revalidate diff. Pass the cached version string (or null). */
  getLibraryDiff: (since: string | null) =>
    routeLocalLibrary(
      () => getLocalAdapter().getLibraryDiff(Number(since || 0)).then((d) => ({
        added: d.added,
        modified: d.updated,
        removed: d.removed,
        full_refresh_required: !!d.fullResync,
        to_version: d.version,
      })),
      () => api.get(`/api/library/diff${since ? `?since=${encodeURIComponent(since)}` : ''}`),
    ),
  getLocalCapabilities: () =>
    shouldUseLocalLibraryIpc()
      ? getLocalCapabilities()
      : Promise.resolve({
          enrichment: true,
          bulkRepair: true,
          duplicateManagement: true,
          folderManagement: true,
          scanStatus: true,
          artworkManagement: true,
        }),
  getLocalHealthStatus: () =>
    shouldUseLocalLibraryIpc()
      ? getLocalHealthStatusCached()
      : Promise.resolve({
          status: 'unknown',
          message: null,
          lastRunAt: null,
          mappedLegacyCount: null,
          unresolvedLegacyCount: null,
        }),
  /** Backfill album created_at from earliest track timestamps. */
  fixAlbumDates: () => api.post('/api/library/albums/fix-dates'),

  saveDuplicateStatus: (updates: { album_key: string; status: string | null }[]) =>
    api.post('/api/library/albums/duplicate-status', { updates }),

  getDuplicateResolutions: () =>
    api.get('/api/library/albums/duplicate-resolutions'),

  /** @deprecated — use resetAccountChunked() instead */
  resetAccountLegacy: () => api.post('/api/account/reset', { confirm: 'RESET' }),

  resetAccountChunked: async (onProgress?: (msg: string) => void) => {
    onProgress?.('Removing library sources and tracks...');
    const result = await libraryService.removeAllChunked((p) => {
      onProgress?.(`Removing source ${p.currentSource} of ${p.totalSources}: ${p.sourceName} (${p.tracksRemoved} tracks deleted)`);
    });

    onProgress?.('Clearing account data...');
    const resp = await api.post('/api/account/reset', { confirm: 'RESET' });

    const errors = [...(result.errors || [])];
    const cleared = resp?.cleared || {};
    for (const [table, status] of Object.entries(cleared)) {
      if (typeof status === 'string' && status.startsWith('error:')) {
        errors.push(`${table}: ${status}`);
      }
    }

    return { success: true, errors, cleared };
  },
};
